import React, { useState, useEffect, useRef } from 'react';
import { Search, Loader2, X, Zap } from 'lucide-react';
import { UnidadeConsumidora } from '../../types';
import { supabase } from '../../src/lib/supabase';
import { enderecoLegadoUtil } from '../../src/lib/dadosDocumentoUc';

export const composeEnderecoUC = (uc: Partial<UnidadeConsumidora>): string => {
    const cidadeUf = uc.cidade ? `${uc.cidade}${uc.estado ? `/${uc.estado}` : ''}` : '';
    const partes = [uc.logradouro, uc.numero, uc.bairro, cidadeUf].filter(Boolean);
    if (partes.length > 0) return partes.join(', ');
    return enderecoLegadoUtil(uc.endereco);
};

// Tudo o que os modais de documento leem da UC escolhida. Faltando desconto e
// outro_numero_uc, o aditivo de desconto mostrava "desconto atual: não informado"
// e o distrato não achava o saldo da UC pelo segundo número.
const CAMPOS_BUSCA = 'id, numero_uc, outro_numero_uc, nome_cliente, cpf_cnpj_titular, cpf_cnpj_cliente, titular_fatura, email_cliente, outro_email, whatsapp_cliente, whatsapp_cliente_2, cep, logradouro, numero, bairro, cidade, estado, endereco, concessionaria, situacao, negociacao_id, desconto_tarifa, desconto_bandeira';

interface UCSearchPickerProps {
    selecionadas: UnidadeConsumidora[];
    onAdd: (uc: UnidadeConsumidora) => void;
    onRemove: (uc: UnidadeConsumidora) => void;
    max: number;
    placeholder?: string;
}

const UCSearchPicker: React.FC<UCSearchPickerProps> = ({ selecionadas, onAdd, onRemove, max, placeholder }) => {
    const [query, setQuery] = useState('');
    const [results, setResults] = useState<UnidadeConsumidora[]>([]);
    const [searching, setSearching] = useState(false);
    const [showDropdown, setShowDropdown] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                setShowDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        if (query.trim().length < 2) {
            setResults([]);
            return;
        }
        const timer = setTimeout(async () => {
            setSearching(true);
            try {
                const term = query.trim().replace(/[%,()]/g, '');
                const { data, error } = await supabase
                    .from('unidades_consumidoras')
                    .select(CAMPOS_BUSCA)
                    .or(`nome_cliente.ilike.%${term}%,numero_uc.ilike.%${term}%,cpf_cnpj_titular.ilike.%${term}%,titular_fatura.ilike.%${term}%`)
                    .limit(10);
                if (error) throw error;
                setResults((data as UnidadeConsumidora[]) || []);
                setShowDropdown(true);
            } catch (err) {
                console.error('Erro ao buscar UCs:', err);
                setResults([]);
            } finally {
                setSearching(false);
            }
        }, 350);
        return () => clearTimeout(timer);
    }, [query]);

    const atingiuMax = selecionadas.length >= max;

    return (
        <div ref={containerRef} className="space-y-3">
            <div className="relative">
                <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 dark:text-slate-500">
                    {searching ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />}
                </div>
                <input
                    type="text"
                    value={query}
                    onChange={e => setQuery(e.target.value)}
                    onFocus={() => results.length > 0 && setShowDropdown(true)}
                    disabled={atingiuMax}
                    placeholder={atingiuMax ? `Limite de ${max} UC(s) atingido` : (placeholder || 'Buscar por nome do cliente, nº da UC ou CPF/CNPJ...')}
                    className="w-full border border-gray-200 dark:border-slate-700 rounded-xl pl-10 pr-4 py-2.5 text-sm focus:border-blue-500 outline-none bg-white dark:bg-slate-900 dark:text-slate-100 disabled:opacity-60 disabled:cursor-not-allowed"
                />
                {showDropdown && results.length > 0 && (
                    <div className="absolute z-20 mt-1 w-full bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl shadow-xl max-h-64 overflow-y-auto">
                        {results.map(uc => {
                            const jaSelecionada = selecionadas.some(s => s.id === uc.id);
                            return (
                                <button
                                    key={uc.id}
                                    type="button"
                                    disabled={jaSelecionada}
                                    onClick={() => {
                                        onAdd(uc);
                                        setShowDropdown(false);
                                        setQuery('');
                                    }}
                                    className="w-full text-left px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-slate-700/60 transition-colors border-b border-gray-50 dark:border-slate-700/50 last:border-b-0 disabled:opacity-40 disabled:cursor-not-allowed"
                                >
                                    <div className="flex items-center justify-between gap-2">
                                        <span className="text-sm font-semibold text-gray-800 dark:text-slate-100 truncate">{uc.nome_cliente}</span>
                                        <span className="text-xs font-mono text-gray-500 dark:text-slate-400 flex-shrink-0">UC {uc.numero_uc}</span>
                                    </div>
                                    <p className="text-xs text-gray-500 dark:text-slate-400 truncate mt-0.5">
                                        {composeEnderecoUC(uc) || 'Sem endereço cadastrado'}
                                        {uc.situacao ? ` • ${uc.situacao}` : ''}
                                    </p>
                                </button>
                            );
                        })}
                    </div>
                )}
                {showDropdown && !searching && results.length === 0 && query.trim().length >= 2 && (
                    <div className="absolute z-20 mt-1 w-full bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl shadow-xl px-4 py-3">
                        <p className="text-sm text-gray-500 dark:text-slate-400">Nenhuma UC encontrada.</p>
                    </div>
                )}
            </div>

            {selecionadas.length > 0 && (
                <div className="flex flex-wrap gap-2">
                    {selecionadas.map(uc => (
                        <div
                            key={uc.id}
                            className="flex items-center gap-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800/50 rounded-lg pl-2.5 pr-1.5 py-1.5"
                        >
                            <Zap size={13} className="text-blue-500 flex-shrink-0" />
                            <div className="min-w-0">
                                <p className="text-xs font-semibold text-gray-800 dark:text-slate-100 leading-tight">UC {uc.numero_uc}</p>
                                <p className="text-[11px] text-gray-500 dark:text-slate-400 leading-tight truncate max-w-[180px]">{uc.nome_cliente}</p>
                            </div>
                            <button
                                type="button"
                                onClick={() => onRemove(uc)}
                                className="p-1 text-gray-400 hover:text-red-500 rounded transition-colors flex-shrink-0"
                            >
                                <X size={13} />
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default UCSearchPicker;
